<template>
  <div class="min-h-screen">
    <PageHeader
      title="Teams Meeting Debug"
      subtitle="View and debug Microsoft Teams meetings for users"
    />

    <!-- User Selector -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="bg-white rounded-lg border border-gray-200">
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex items-center gap-4">
            <label for="user-select" class="text-sm font-medium text-gray-700">
              Select User:
            </label>
            <select
              id="user-select"
              v-model="selectedAppUserID"
              @change="handleUserChange"
              class="block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-dark sm:text-sm"
            >
              <option value="" disabled>Choose a user...</option>
              <option v-for="user in appusers" :key="user.id" :value="user.id" class="sensitive">
                {{ user.last_name }}, {{ user.first_name }}
              </option>
            </select>
            <div v-if="selectedAppUserID" class="flex items-center gap-2 text-sm text-gray-600">
              <svg class="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <span>User selected</span>
            </div>
          </div>
        </div>

        <!-- Meetings List -->
        <div v-if="selectedAppUserID">
          <TeamsMeetingList
            :key="meetingListKey"
            :app-user-id="selectedAppUserID"
            :empty-state-message="customEmptyMessage"
            @meeting-selected="handleMeetingSelected"
            @meetings-loaded="handleMeetingsLoaded"
          >
          </TeamsMeetingList>
        </div>

        <!-- No User Selected State -->
        <div v-else class="px-6 py-12 text-center">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900">No user selected</h3>
          <p class="mt-1 text-sm text-gray-500">Please select a user to view their Teams meetings.</p>
        </div>
      </div>

      <!-- Selected Meeting Details (Debug Info) -->
      <div v-if="selectedMeeting" class="mt-8 bg-white rounded-lg shadow">
        <ComponentHeader title="Meeting Details" />
        <div class="px-6 py-4">
          <pre class="bg-gray-50 rounded-md p-4 text-xs overflow-x-auto">{{ JSON.stringify(selectedMeeting, null, 2) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import TeamsMeetingList from '@/components/widgetcomponents/TeamsMeetingList.vue';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';

export default {
  components: {
    TeamsMeetingList,
    PageHeader,
    ComponentHeader,
  },
  data() {
    return {
      selectedAppUserID: null,
      appusers: [],
      selectedMeeting: null,
      meetingListKey: 0,
      meetingStats: null,
    };
  },
  computed: {
    customEmptyMessage() {
      return this.selectedAppUserID
        ? 'No Teams meetings found for this user in the selected date range'
        : 'Please select a user to view their meetings';
    }
  },
  mounted() {
    this.fetchAppUsers();
  },
  methods: {
    fetchAppUsers() {
      axios
        .get('/api/user/')
        .then(response => {
          this.appusers = response.data
            .sort((a, b) =>
              a.last_name.localeCompare(b.last_name) ||
              a.first_name.localeCompare(b.first_name)
            );
        })
        .catch(error => console.error('Error fetching users:', error));
    },

    handleUserChange() {
      // Reset selected meeting and stats when user changes
      this.selectedMeeting = null;
      this.meetingStats = null;
      // Force remount of TeamsMeetingList component
      this.meetingListKey++;
    },

    handleMeetingSelected(meeting) {
      this.selectedMeeting = meeting;
      console.log('Meeting selected:', meeting);
    },

    handleMeetingsLoaded(meetings) {
      // Calculate stats for debugging
      this.meetingStats = {
        total: meetings.length,
        online: meetings.filter(m => m.onlineMeeting?.joinUrl).length,
        withTranscripts: meetings.filter(m => m.hasTranscript === true).length,
        cancelled: meetings.filter(m => m.isCancelled).length,
      };
      console.log('Meetings loaded:', meetings.length, 'meetings');
    }
  },
};
</script>

<style scoped>
/* Custom select styling */
select {
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}
</style>
